import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { useModalInsets } from "@/src/components/mobile/useModalInsets";

import { NativeStackScreenProps } from "@react-navigation/native-stack";
import React, { useMemo, useState } from "react";
import { useLazyLoadQuery, useMutation } from "react-relay";
import { graphql } from "relay-runtime";
import { TopicsAdminDetailQuery } from "./__generated__/TopicsAdminDetailQuery.graphql";
import { TopicsAdminDetailSaveRoutingMutation } from "./__generated__/TopicsAdminDetailSaveRoutingMutation.graphql";
import { TopicAdminStackParamList } from "./TopicsAdmin";
import {
  TeamSelectList,
  TeamSelectListWholeStoreOption,
} from "widgets/src/TeamSelectList";

export function TopicsAdminDetailScreen(
  props: NativeStackScreenProps<TopicAdminStackParamList, "TopicAdminDetail">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => (
        <TopicAdminDetail
          fetchKey={fk}
          topicId={props.route.params.topicId}
          navigation={props.navigation}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
function TopicAdminDetail(props: {
  fetchKey: string;
  topicId: string;
  navigation: NativeStackScreenProps<
    TopicAdminStackParamList,
    "TopicAdminDetail"
  >["navigation"];
}) {
  const insets = useModalInsets();
  const data = useLazyLoadQuery<TopicsAdminDetailQuery>(
    graphql`
      query TopicsAdminDetailQuery($topicId: ID!) {
        storeTopic(id: $topicId) @required(action: THROW) {
          id
          name
          isPrivate
          description
          potentialTeams {
            ...TeamSelectList_Team
          }
          teams {
            id
          }
        }
      }
    `,
    { topicId: props.topicId },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const topicTeamIds = useMemo(
    () => data.storeTopic?.teams?.map((team) => team.id) ?? [],
    [data.storeTopic?.teams]
  );
  const [teams, setTeams] = useState(topicTeamIds);

  const isSameTeams = useMemo(
    () =>
      teams.length === topicTeamIds.length &&
      topicTeamIds.every((team) => teams.includes(team)),
    [topicTeamIds, teams]
  );

  const [saveRouting, saveRoutingLoading] =
    useMutation<TopicsAdminDetailSaveRoutingMutation>(graphql`
      mutation TopicsAdminDetailSaveRoutingMutation(
        $topicId: ID!
        $teamIds: [ID!]!
      ) {
        store {
          setTopicTeams(topicId: $topicId, teamIds: $teamIds) {
            name
            description
            teams {
              id
            }
          }
        }
      }
    `);

  useNavigationHeader({
    type: "SAVE",
    onSave() {
      saveRouting({
        variables: {
          topicId: props.topicId,
          teamIds: teams,
        },
        onCompleted() {
          props.navigation.goBack();
        },
      });
    },
    onCancel() {
      props.navigation.goBack();
    },
    saveLoading: saveRoutingLoading,
    disabled: isSameTeams,
    headerTitle: data.storeTopic?.name,
  });

  return (
    <TeamSelectList
      insets={insets}
      contentBox={{ spaceTop: "medium" }}
      teams={data.storeTopic?.potentialTeams ?? []}
      selected={teams}
      ListHeaderComponent={
        data.storeTopic.isPrivate ? undefined : (
          <TeamSelectListWholeStoreOption
            onSelect={() => setTeams([])}
            selected={teams.length === 0}
          />
        )
      }
      onItemPress={(teamId, selected) => {
        setTeams((prev) =>
          !selected ? [...prev, teamId] : prev.filter((id) => id !== teamId)
        );
      }}
    />
  );
}
